/** Small async hooks over the static data loaders. */
import { useEffect, useState } from "react";

import type { CatalogEntry, ThemesData, VideoDoc } from "../lib/types";

import { loadCatalog, loadThemes, loadVideo } from "./api";

interface CatalogState {
  readonly entries: readonly CatalogEntry[] | null;
  readonly error: boolean;
}

/** Load the catalogue once and hold it. */
export function useCatalog(): CatalogState {
  const [entries, setEntries] = useState<readonly CatalogEntry[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    loadCatalog().then(
      (loaded) => {
        if (active) {
          setEntries(loaded);
        }
      },
      () => {
        if (active) {
          setError(true);
        }
      },
    );
    return () => {
      active = false;
    };
  }, []);
  return { entries, error };
}

/** Load the three theme lists once. */
export function useThemes(): ThemesData | null {
  const [themes, setThemes] = useState<ThemesData | null>(null);
  useEffect(() => {
    let active = true;
    loadThemes().then(
      (loaded) => {
        if (active) {
          setThemes(loaded);
        }
      },
      () => {
        if (active) {
          setThemes({ harmony: [], wellbeing: [], peace: [] });
        }
      },
    );
    return () => {
      active = false;
    };
  }, []);
  return themes;
}

interface VideoState {
  readonly video: VideoDoc | null;
  readonly loading: boolean;
  readonly missing: boolean;
}

/** Load one video's record and transcript, refetching when the id changes. */
export function useVideo(id: string): VideoState {
  const [state, setState] = useState<VideoState>({
    video: null,
    loading: true,
    missing: false,
  });
  useEffect(() => {
    let active = true;
    setState({ video: null, loading: true, missing: false });
    loadVideo(id).then(
      (doc) => {
        if (active) {
          setState({ video: doc, loading: false, missing: doc === null });
        }
      },
      () => {
        if (active) {
          setState({ video: null, loading: false, missing: true });
        }
      },
    );
    return () => {
      active = false;
    };
  }, [id]);
  return state;
}
